import React, { useEffect, useState } from "react";
import MobileLayout from "../components/layout/MobileLayout";
import PageHeader from "../components/PageHeader";
import { api } from "../lib/api";
import { Copy, Share2, CheckCheck, Gift, Users, Wallet, History, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * Refer & Earn — totally new design (not the dark royal theme).
 * Vibrant coral→orange hero + white body, dashed "coupon" style code block,
 * horizontal step strip, and bright share pills. Reads like a scratch-card invite.
 */
export default function ReferEarn() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api.get("/users/me/referral").then(r => setData(r.data)); }, []);

  const code = data?.referral_code || "";
  const pct = data?.percent ?? 5;
  const earned = Number(data?.total_earned ?? 0);
  // Share the APK download page so friends install the app directly.
  // Pass the code via ?ref= so the referral is auto-filled at signup.
  const shareUrl = code ? `${window.location.origin}/download?ref=${code}` : `${window.location.origin}/download`;
  const shareText = `🎰 REDDY CLUB — Jeeto Bada!\n\nMera Reddy Code: ${code}\n\n👉 App download karo + signup pe pehli deposit pe ${pct}% EXTRA bonus pao!\n\n${shareUrl}`;

  const copyCode = () => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Reddy code copied! Ab share karo 🎁");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareNow = async () => {
    if (navigator.share && shareUrl) {
      try { await navigator.share({ title: "Reddy Club", text: shareText, url: shareUrl }); } catch (e) {}
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success("Share message copied");
    }
  };

  const waShare = () => {
    if (!shareUrl) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
  };

  return (
    <MobileLayout>
      <PageHeader title="Refer & Earn" testId="refer-header" />
      <div className="px-3 pb-8">

      {/* HERO — coral/orange sunset band */}
      <div
        className="relative overflow-hidden rounded-3xl mb-4 shadow-lg"
        style={{ background: "linear-gradient(135deg,#ff6b6b 0%,#ff8e3c 55%,#ffb347 100%)" }}
        data-testid="refer-hero"
      >
        {/* soft circles */}
        <div className="absolute -top-10 -right-8 w-40 h-40 rounded-full bg-white/15" />
        <div className="absolute -bottom-14 -left-10 w-44 h-44 rounded-full bg-black/10" />

        <div className="relative px-5 pt-6 pb-5 flex items-start gap-4">
          {/* Illustration badge */}
          <div className="shrink-0 w-20 h-20 rounded-2xl bg-white/95 flex items-center justify-center shadow-xl rotate-[-6deg]">
            <Gift className="w-11 h-11 text-rose-500" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0 text-white">
            <div className="text-[11px] font-black tracking-[0.25em] opacity-90">INVITE · EARN</div>
            <h1 className="font-display font-black text-2xl leading-tight mt-0.5" data-testid="refer-headline">
              Invite friends,<br/> earn <span className="underline decoration-white/60 decoration-2 underline-offset-2">{pct}% cash</span>
            </h1>
            <p className="text-xs text-white/90 mt-1.5 leading-snug">
              Har dost ki pehli deposit pe <strong className="font-black">{pct}%</strong> turant aapke wallet mein credit
            </p>
          </div>
        </div>

        {/* Lifetime strip inside hero */}
        <div className="relative bg-black/15 backdrop-blur-sm px-5 py-3 flex items-center justify-between" data-testid="refer-lifetime">
          <div>
            <div className="text-[10px] font-black tracking-widest text-white/75">LIFETIME EARNED</div>
            <div className="font-display font-black text-white text-xl tabular-nums leading-tight">
              ₹{earned.toLocaleString("en-IN")}
            </div>
          </div>
          <Link
            to="/bonus-history"
            data-testid="bonus-history-link"
            className="inline-flex items-center gap-1 bg-white text-rose-600 text-xs font-black px-3 py-1.5 rounded-full shadow active:scale-95"
          >
            <History className="w-3.5 h-3.5" />
            HISTORY
          </Link>
        </div>
      </div>

      {/* COUPON — dashed border with cut-out notches */}
      <div className="relative mb-4" data-testid="referral-code-box">
        {/* side notches — match page bg */}
        <span className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full" style={{ background: "#060a1b" }} />
        <span className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full" style={{ background: "#060a1b" }} />
        <div
          className="relative rounded-2xl px-4 py-5 backdrop-blur-sm"
          style={{
            background: "linear-gradient(180deg, rgba(10,24,51,0.65) 0%, rgba(6,14,40,0.9) 100%)",
            border: "2px dashed rgba(251,113,133,0.5)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(251,113,133,0.08) inset",
          }}
        >
          <div className="text-[10px] font-black tracking-[0.3em] text-rose-300 text-center">YOUR INVITE CODE</div>
          <div
            className="mt-2 font-display font-black text-center text-gold-static tabular-nums tracking-[0.18em] text-3xl sm:text-4xl select-all"
            data-testid="referral-code-value"
          >
            {code || "——————"}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              onClick={copyCode}
              data-testid="copy-code-btn"
              className={`inline-flex items-center justify-center gap-2 h-11 rounded-xl font-black tracking-wider active:scale-95 transition ${
                copied
                  ? "bg-emerald-500 text-white"
                  : "bg-cyan-500 text-white hover:bg-cyan-400 shadow-[0_0_14px_rgba(34,211,238,0.4)]"
              }`}
            >
              {copied ? <><CheckCheck className="w-4 h-4" /> COPIED</> : <><Copy className="w-4 h-4" /> COPY CODE</>}
            </button>
            <button
              onClick={shareNow}
              data-testid="share-now-btn"
              className="inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-rose-500 text-white font-black tracking-wider active:scale-95 transition hover:bg-rose-600 shadow-[0_0_14px_rgba(244,63,94,0.4)]"
            >
              <Share2 className="w-4 h-4" /> SHARE
            </button>
          </div>
        </div>
      </div>

      {/* WhatsApp big pill */}
      <button
        onClick={waShare}
        data-testid="share-whatsapp-btn"
        className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-white font-black py-3 rounded-2xl shadow-md active:scale-[0.98] transition mb-4 hover:brightness-110"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654z"/></svg>
        SHARE ON WHATSAPP
      </button>

      {/* HOW IT WORKS — horizontal mini-strip, totally different vibe */}
      <div className="mb-4" data-testid="how-it-works">
        <div className="text-[11px] font-black tracking-[0.25em] text-cyan-200/70 mb-2 pl-1">HOW IT WORKS</div>
        <div className="grid grid-cols-4 gap-2">
          <Step n="1" Icon={Share2} title="Share"   tint="bg-sky-500/15 text-sky-300 border-sky-400/40"           />
          <Step n="2" Icon={Users}  title="Signup"  tint="bg-indigo-500/15 text-indigo-300 border-indigo-400/40"  />
          <Step n="3" Icon={Wallet} title="Deposit" tint="bg-amber-500/15 text-amber-300 border-amber-400/40"     />
          <Step n="4" Icon={Gift}   title="Earn"    tint="bg-emerald-500/15 text-emerald-300 border-emerald-400/40" />
        </div>
      </div>

      {/* Fine print */}
      <div className="text-center text-[11px] text-cyan-200/60 mt-5">
        Unlimited referrals · {pct}% on friend&apos;s first deposit only
      </div>
      </div>
    </MobileLayout>
  );
}

function Step({ n, Icon, title, tint }) {
  return (
    <div
      className="relative rounded-xl p-2.5 text-center shadow-lg backdrop-blur-sm"
      style={{
        background: "linear-gradient(180deg, rgba(10,24,51,0.65) 0%, rgba(6,14,40,0.9) 100%)",
        border: "1px solid rgba(34,211,238,0.25)",
      }}
    >
      <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center shadow">
        {n}
      </div>
      <div className={`mx-auto w-9 h-9 rounded-full border ${tint} flex items-center justify-center`}>
        <Icon className="w-4 h-4" strokeWidth={2.5} />
      </div>
      <div className="mt-1 text-[11px] font-black text-cyan-100 tracking-wide">{title}</div>
    </div>
  );
}
